import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { getStatusColor, formatTime, formatDays } from '../utils/helpers';
import { Calendar, Clock, BarChart2, AlertCircle, HelpCircle, ClipboardList } from 'lucide-react';

const MyReports = ({ user }) => {
  const [reports, setReports] = useState([]); 
  const [leaveRequests, setLeaveRequests] = useState([]); 
  const [lateRequests, setLateRequests] = useState([]); 
  const [forgotScanRequests, setForgotScanRequests] = useState([]); 
  const [summaryReport, setSummaryReport] = useState(null); 

  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [activeTab, setActiveTab] = useState('summary');

  const loadReports = useCallback(async () => {
    try {
      const startYear = new Date(dateRange.from).getFullYear();
      const endYear = new Date(dateRange.to).getFullYear();
      const yearsToFetch = new Set([startYear, endYear]); 
      
      const holidayPromises = Array.from(yearsToFetch).map(year =>
        api.fetch(`holidays?year=${year}`)
      );
      
      const [holidayResults, attendanceData] = await Promise.all([
        Promise.all(holidayPromises),
        api.getReports({
          type: 'daily',
          date_from: dateRange.from,
          date_to: dateRange.to,
          employee_id: user.employee_id
        })
      ]);

      const holidayDates = new Set(
        holidayResults.flat().map(h => h.holiday_date)
      );

      const attendanceMap = {};
      if (attendanceData && attendanceData.length > 0) {
        attendanceData.forEach(record => {
          if (record.work_date) {
            attendanceMap[record.work_date] = record;
          }
        });
      }

      const workingDates = [];
      const startDate = new Date(dateRange.from);
      const endDate = new Date(dateRange.to);
      
      let currentDate = new Date(startDate);
      currentDate.setUTCHours(12, 0, 0, 0); 
      endDate.setUTCHours(12, 0, 0, 0);

      while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split('T')[0];
        const dayOfWeek = currentDate.getUTCDay(); // 0 = Sunday, 6 = Saturday
        const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);
        const isHoliday = holidayDates.has(dateString);

        if (!isWeekend && !isHoliday) {
          workingDates.push(dateString);
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const fullReport = workingDates.map(dateStr => {
        if (attendanceMap[dateStr]) {
          const record = attendanceMap[dateStr];
          const isCheckInMissing = !record.check_in || record.check_in === '-';
          
          if (isCheckInMissing && record.status === 'ปกติ') {
             return { ...record, status: 'ไม่ลงเวลาเข้า' };
          }
          return record;
        } else {
          return {
            work_date: dateStr,
            check_in: null,
            check_out: null,
            status: 'ไม่มาปฏิบัติงาน',
            notes: null,
            attendance_notes: null
          };
        }
      });

      fullReport.sort((a, b) => new Date(b.work_date) - new Date(a.work_date));
      return fullReport;

    } catch (error) {
      console.error('Error loading reports:', error);
      return []; 
    }
  }, [dateRange, user.employee_id]);

  const loadLeaveReports = useCallback(async () => {
    try {
      const data = await api.getLeaveRequests(user.employee_id);
      const filteredData = data.filter(req => {
        const reqStart = new Date(req.start_date);
        const reqEnd = new Date(req.end_date);
        const filterStart = new Date(dateRange.from);
        const filterEnd = new Date(dateRange.to);
        return reqStart <= filterEnd && reqEnd >= filterStart;
      });
      filteredData.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      return filteredData;
    } catch (error) {
      console.error('Error loading leave reports:', error);
      return [];
    }
  }, [dateRange, user.employee_id]);

  const loadLateReports = useCallback(async () => {
    try {
      const data = await api.getLateRequests(user.employee_id);
      const filteredData = data.filter(req => {
        const reqDate = new Date(req.request_date);
        const filterStart = new Date(dateRange.from);
        const filterEnd = new Date(dateRange.to);
        return reqDate >= filterStart && reqDate <= filterEnd;
      });
      filteredData.sort((a, b) => new Date(b.request_date) - new Date(a.request_date));
      return filteredData;
    } catch (error) {
      console.error('Error loading late reports:', error);
      return [];
    }
  }, [dateRange, user.employee_id]);

  const loadForgotScanReports = useCallback(async () => {
    try {
      const data = await api.getForgotScanRequests(user.employee_id);
      const filteredData = data.filter(req => {
        const reqDate = new Date(req.request_date);
        const filterStart = new Date(dateRange.from);
        const filterEnd = new Date(dateRange.to);
        return reqDate >= filterStart && reqDate <= filterEnd;
      });
      filteredData.sort((a, b) => new Date(b.request_date) - new Date(a.request_date));
      return filteredData;
    } catch (error) {
      console.error('Error loading forgot scan reports:', error);
      return [];
    }
  }, [dateRange, user.employee_id]);

  const loadSummaryStats = useCallback(async () => {
    try {
      const data = await api.getReports({
        type: 'summary',
        date_from: dateRange.from,
        date_to: dateRange.to,
        employee_id: user.employee_id
      });
      return data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error loading summary stats:', error);
      return null;
    }
  }, [dateRange, user.employee_id]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      const [attendanceData, leaveData, lateData, forgotScanData, summaryData] = await Promise.all([
        loadReports(),
        loadLeaveReports(),
        loadLateReports(),
        loadForgotScanReports(),
        loadSummaryStats()
      ]);
      setReports(attendanceData);
      setLeaveRequests(leaveData);
      setLateRequests(lateData);
      setForgotScanRequests(forgotScanData);
      setSummaryReport(summaryData);
      setLoading(false);
    };
    
    loadAllData();
  }, [loadReports, loadLeaveReports, loadLateReports, loadForgotScanReports, loadSummaryStats]);

  const getScanTypeLabel = (scanType) => {
    if (scanType === 'in') return 'ลืมลงเวลาเข้า';
    if (scanType === 'out') return 'ลืมลงเวลาออก';
    return scanType;
  };

  const getStatusLabel = (status) => {
    if (status === 'approved') return <span className="text-green-600 font-medium">อนุมัติ</span>;
    if (status === 'rejected') return <span className="text-red-600 font-medium">ไม่อนุมัติ</span>;
    if (status === 'pending') return <span className="text-yellow-600 font-medium">รออนุมัติ</span>;
    return status;
  };

  const getRecordCount = () => {
    switch (activeTab) {
      case 'attendance': return reports.length;
      case 'leave': return leaveRequests.length;
      case 'late': return lateRequests.length;
      case 'forgotScan': return forgotScanRequests.length;
      case 'summary': return summaryReport ? 1 : 0;
      default: return 0;
    }
  };

  return (
    <div className="space-y-8">
      
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          รายงานของฉัน
        </h1>
        <p className="text-gray-600 mt-2">
          ตรวจสอบสถิติการเข้างานและประวัติการลาของคุณ
        </p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <BarChart2 className="h-6 w-6 mr-2 text-blue-500" />
            ตัวกรองรายงาน
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-row sm:items-center sm:space-x-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">จากวันที่</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="col-span-2 sm:col-span-1 sm:pt-6 flex items-center h-full">
              <div className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg">
                พบข้อมูล {getRecordCount()} รายการ
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-8 px-6 min-w-max" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardList className={`h-5 w-5 ${activeTab === 'summary' ? 'text-blue-500' : 'text-gray-400'}`} />
              <span>สรุป</span>
            </button>
            <button
              onClick={() => setActiveTab('attendance')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'attendance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className={`h-5 w-5 ${activeTab === 'attendance' ? 'text-blue-500' : 'text-gray-400'}`} />
              <span>รายงานการเข้างาน</span>
            </button>
            <button
              onClick={() => setActiveTab('leave')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'leave'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className={`h-5 w-5 ${activeTab === 'leave' ? 'text-blue-500' : 'text-gray-400'}`} />
              <span>รายงานการลา</span>
            </button>
            <button
              onClick={() => setActiveTab('late')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'late'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className={`h-5 w-5 ${activeTab === 'late' ? 'text-blue-500' : 'text-gray-400'}`} />
              <span>รายงานการมาสาย</span>
            </button>
            <button
              onClick={() => setActiveTab('forgotScan')}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'forgotScan'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <HelpCircle className={`h-5 w-5 ${activeTab === 'forgotScan' ? 'text-blue-500' : 'text-gray-400'}`} />
              <span>รายงานลืมลงเวลา</span>
            </button>
          </nav>
        </div>
        
        <div className="p-0 sm:p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div>
              {/* --- TAB: ATTENDANCE --- */}
              {activeTab === 'attendance' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วัน</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เข้างาน</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ออกงาน</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">หมายเหตุ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reports.length > 0 ? reports.map((record, index) => {
                        // [✅ เพิ่มตัวแปรตรวจสอบสถานะ ซ่อนเวลาถ้าเป็น ไปราชการ หรือ ลา]
                        const shouldHideTime = ['ไปราชการ', 'ไม่มาปฏิบัติงาน'].includes(record.status) || (record.status && record.status.startsWith('ลา'));
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.work_date ? new Date(record.work_date).toLocaleDateString('th-TH') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {record.work_date ? new Date(record.work_date).toLocaleDateString('th-TH', { weekday: 'short' }) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {/* [✅ ปรับการแสดงผลเวลาเข้า] */}
                              {shouldHideTime ? '-' : formatTime(record.check_in)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {/* [✅ ปรับการแสดงผลเวลาออก] */}
                              {shouldHideTime ? '-' : formatTime(record.check_out)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {record.notes || record.attendance_notes || '-'}
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                            <Clock className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                            ไม่พบข้อมูลการเข้างานในช่วงวันที่ที่เลือก
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* --- TAB: LEAVE --- */}
              {activeTab === 'leave' && (
                <div>
                  {leaveRequests.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                      ไม่พบข้อมูลการลาในช่วงวันที่ที่เลือก
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ประเภทการลา</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ตั้งแต่วันที่</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ถึงวันที่</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนวัน</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เหตุผล</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {leaveRequests.map((req) => (
                            <tr key={req.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(req.leave_type)}`}>
                                  {req.leave_type}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(req.start_date).toLocaleDateString('th-TH')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(req.end_date).toLocaleDateString('th-TH')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center bg-gray-50/50">
                                {formatDays(req.total_days)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getStatusLabel(req.status)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={req.reason}>
                                {req.reason || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* --- TAB: LATE --- */}
              {activeTab === 'late' && (
                <div>
                  {lateRequests.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <AlertCircle className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                      ไม่พบข้อมูลการยื่นขอมาสายในช่วงวันที่ที่เลือก
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่มาสาย</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เหตุผล</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {lateRequests.map((req) => (
                            <tr key={req.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {new Date(req.request_date).toLocaleDateString('th-TH')}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate" title={req.reason}>
                                {req.reason || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getStatusLabel(req.status)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* --- TAB: FORGOT SCAN --- */}
              {activeTab === 'forgotScan' && (
                <div>
                  {forgotScanRequests.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <HelpCircle className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                      ไม่พบข้อมูลการยื่นลืมลงเวลาในช่วงวันที่ที่เลือก
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่ลืมสแกน</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ประเภท</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เหตุผล</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {forgotScanRequests.map((req) => (
                            <tr key={req.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {new Date(req.request_date).toLocaleDateString('th-TH')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                  req.scan_type === 'in' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                                }`}>
                                  {getScanTypeLabel(req.scan_type)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate" title={req.reason}>
                                {req.reason || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {getStatusLabel(req.status)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              
              {/* --- TAB: SUMMARY --- */}
              {activeTab === 'summary' && (
                <div className="p-4 sm:p-6 bg-white">
                  {!summaryReport ? (
                    <div className="py-12 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                       <ClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                       <p>ไม่พบข้อมูลสรุปในช่วงวันที่ที่เลือก</p>
                    </div>
                  ) : (
                    <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th rowSpan="2" className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase border-r border-b tracking-wider bg-gray-100">มาสาย</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r border-b tracking-wider bg-blue-50/50">ลาป่วย</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r border-b tracking-wider bg-green-50/50">ลากิจ</th>
                            <th colSpan="2" className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r border-b tracking-wider bg-purple-50/50">ไปราชการ</th>
                            {/* [✅ เปลี่ยนหัวคอลัมน์จาก "ขาด" เป็น "ไม่มาปฏิบัติงาน"] */}
                            <th rowSpan="2" className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase border-l border-b tracking-wider bg-red-50/50">ไม่มาปฏิบัติงาน</th>
                          </tr>
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">ครั้ง</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">วัน</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">ครั้ง</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">วัน</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">ครั้ง</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 border-r border-b bg-white">วัน</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-6 text-lg text-center text-red-600 font-bold border-r">
                              {summaryReport.late_days || '0'}
                            </td>
                            
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-blue-50/10">
                              {summaryReport.sick_leave_times || '0'}
                            </td>
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-blue-50/10">
                              {formatDays(summaryReport.sick_leave_days)}
                            </td>
                            
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-green-50/10">
                              {summaryReport.personal_leave_times || '0'}
                            </td>
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-green-50/10">
                              {formatDays(summaryReport.personal_leave_days)}
                            </td>
                            
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-purple-50/10">
                              {summaryReport.official_leave_times || '0'}
                            </td>
                            <td className="px-4 py-6 text-base font-medium text-center text-gray-700 border-r bg-purple-50/10">
                              {formatDays(summaryReport.official_leave_days)}
                            </td>
                            
                            <td className="px-4 py-6 text-lg text-center text-red-600 font-bold border-l bg-red-50/10">
                              {/* [✅ คำนวณใหม่จาก state `reports` ที่ใช้แสดงในตาราง ไม่ดึงจาก API ที่นับรวมไปราชการ] */}
                              {formatDays(reports.filter(r => r.status === 'ไม่มาปฏิบัติงาน').length)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyReports;